import { userManager } from "./sso";
import { User } from "oidc-client";
import { useContext, useEffect, useState, useCallback } from "react";
// import { UserRole } from '../hooks/UserRole';
// import { API_SCOPE, IDP, API_URI } from '../environment-variables';
import usePersistedState from "../hooks/usePersistedState";
import React from "react";
import { constructHeaders } from "./api";
import { API_URI } from "../environmentVariables";
import IUser from "../models/IUser";
import { useLocation } from "react-router-dom";
export interface UserProfile {
    ipaddr: string;
    name: string;
    // roles: UserRole[];
    idp: string | null | undefined;
    username: string;
}

interface AuthContextData {
    authState: AuthState;

    login(): void;
    logout(): void;
    refresh(): void;
}

export interface AuthState {
    initialized: boolean;
    loggedIn: boolean;
    user: IUser | null;
    accessToken: string | null;
}

const initialState = {
    initialized: false,
    loggedIn: false,
    user: null,
    accessToken: null
};

const loggedOutState = {
    initialized: true,
    loggedIn: false,
    user: null,
    accessToken: null
};

const AuthContext = React.createContext<AuthContextData>({
    authState: initialState,
    login: () => {},
    logout: () => {},
    refresh: () => {}
});

const AuthProvider: React.FC = ({ children }) => {
    const location = useLocation();

    const [authState, setAuthState] = usePersistedState<AuthState>(
        initialState,
        "login-state"
    );
    const [loading, setLoading] = useState(true);

    const login = async () => {
        await userManager.clearStaleState();

        //// start the signin process, we will send the current url along with the request,
        //// so that we can return to it after login rather than always showing the projects page.
        await userManager.signinRedirect({ state: location.pathname });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const refresh = async () => {
        await userManager.clearStaleState();

        await userManager.signinSilent();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const logout = useCallback(async () => {
        await userManager.signoutRedirect();
    }, []);

    const userLoaded = async (currentUser: User) => {
        if (currentUser == null || currentUser.expired) {
            setAuthState(loggedOutState);
            return;
        }

        const headers = await constructHeaders();
        const response = await fetch(API_URI + "users/me", {
            headers
        });

        const user = (await response.json()) as IUser;

        if (currentUser != null) {
            if (currentUser.profile != null) {
                if (currentUser.profile.given_name != null) {
                    user.displayName = currentUser.profile.given_name;
                }
            }
        }

        if (user.displayName == null) {
            user.displayName = user.email;
        }

        const newAuthState: AuthState = {
            initialized: true,
            loggedIn: true,
            user: user,
            accessToken: currentUser.access_token
        };

        setAuthState(newAuthState);
    };
    const userUnloaded = () => {
        setAuthState(loggedOutState);
    };
    const accessTokenExpired = () => {
        setAuthState(loggedOutState);
    };

    useEffect(() => {
        userManager.events.addUserLoaded(userLoaded);
        userManager.events.addUserUnloaded(userUnloaded);
        userManager.events.addAccessTokenExpired(accessTokenExpired);

        async function CheckOidcUser() {
            await userManager.clearStaleState();

            var currentUser = await userManager.getUser();

            if (currentUser == null) {
                //// if the user is null then we cannot be logged in
                setAuthState(loggedOutState);
            } else {
                if (currentUser.expired) {
                    //// if the token has expired we also cannot be logged in
                    setAuthState(loggedOutState);

                    await userManager.removeUser();
                }
            }
        }

        //// the auth state may state we are logged in but the actual token may still be invalid
        CheckOidcUser();

        if (loading && authState.initialized) {
            if (!authState.loggedIn) {
                setLoading(false);
            }
        }

        return () => {
            userManager.events.removeUserLoaded(userLoaded);
            userManager.events.removeUserUnloaded(userUnloaded);
            userManager.events.removeAccessTokenExpired(accessTokenExpired);
        };
    }, [
        authState,
        loading,
        logout,
        userLoaded,
        userUnloaded,
        accessTokenExpired,
        setAuthState
    ]);

    return (
        <AuthContext.Provider value={{ authState, login, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
};

const useAuth = () => useContext(AuthContext);

const useMappedPermissions = <InjectedProps extends {}>(
    permissionsMapper: (authState: AuthState) => InjectedProps
) => {
    const { authState } = useAuth();
    return permissionsMapper(authState);
};

export { useAuth, AuthProvider, useMappedPermissions };
